import type { PoolLedger, SettingHistory } from '@myrewards/contracts';
import { formatCount, formatDate, formatMoney, plural } from '../ui';

// Pure helpers for the settings screens: values over time, "as of", fractions and the budget
// ledger. No React, no I/O, so they're tested on their own.

export type HistoryRow = SettingHistory['entries'][number];

/** Today in the organization's time zone (T3: America/Los_Angeles until `org.timeZone` says else). */
export function organizationToday(
  now: Date = new Date(),
  timeZone = 'America/Los_Angeles',
): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/** The date a value applies from: its effective date, or the day it was recorded. */
export function appliesFrom(row: Pick<HistoryRow, 'effectiveFrom' | 'recordedAt'>): string {
  return row.effectiveFrom ?? row.recordedAt.slice(0, 10);
}

/**
 * The value that applied on a date (PRD §17 "As of a date"): the latest value that applies on or
 * before it; when two apply from the same day, the one recorded last. `null` when nothing did.
 */
export function valueOn(rows: readonly HistoryRow[], date: string): HistoryRow | null {
  let best: HistoryRow | null = null;
  for (const row of rows) {
    const from = appliesFrom(row);
    if (from > date) continue;
    if (
      best === null ||
      from > appliesFrom(best) ||
      (from === appliesFrom(best) && row.recordedAt > best.recordedAt)
    )
      best = row;
  }
  return best;
}

export interface StepPoint {
  date: string;
  value: number;
  /** Takes effect after today (an FMV set ahead of its date). */
  upcoming: boolean;
  /** What the change was, when it's worth saying: "top-up for Elena Brightwater" (APP-148). */
  note?: string;
}

/** One point per day a value starts to apply, oldest first; later records win the same day. */
export function stepPoints(
  rows: readonly HistoryRow[],
  numeric: (value: unknown) => number | null,
  today: string,
): StepPoint[] {
  const byDate = new Map<string, HistoryRow>();
  for (const row of rows) {
    const from = appliesFrom(row);
    const seen = byDate.get(from);
    if (seen === undefined || row.recordedAt > seen.recordedAt) byDate.set(from, row);
  }
  return [...byDate.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .flatMap(([date, row]) => {
      const value = numeric(row.value);
      return value === null ? [] : [{ date, value, upcoming: date > today }];
    });
}

export interface Fraction {
  num: number;
  den: number;
}

export function isFraction(value: unknown): value is Fraction {
  return (
    typeof value === 'object' &&
    value !== null &&
    'num' in value &&
    'den' in value &&
    typeof value.num === 'number' &&
    typeof value.den === 'number' &&
    value.den > 0
  );
}

function gcd(a: number, b: number): number {
  return b === 0 ? Math.abs(a) : gcd(b, a % b);
}

/** 2/3 in lowest terms. */
export function reduce(f: Fraction): Fraction {
  const g = gcd(f.num, f.den) || 1;
  return { num: f.num / g, den: f.den / g };
}

/** "2/3" (only ever on Executive settings, never on an award screen). */
export function fractionText(f: Fraction): string {
  const r = reduce(f);
  return `${String(r.num)}/${String(r.den)}`;
}

/** "66.67%", "50%": up to two decimals, none when whole. */
export function percentText(f: Fraction): string {
  const pct = (f.num / f.den) * 100;
  const rounded = Math.round(pct * 100) / 100;
  return `${rounded.toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;
}

/** "$77.95": FMV always shows its cents. */
export function formatPerShare(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** The FMV as History shows it ("$77.95"), or "Not set". */
export const fmvText = (value: unknown): string =>
  typeof value === 'number' ? formatPerShare(value) : 'Not set';

/** The FMV as a number for History's chart, or null. */
export const fmvNumber = (value: unknown): number | null =>
  typeof value === 'number' ? value : null;

/** A list's History entry ("Added Jordan Ellis"): lists kept in tables record one person a time. */
export function listChangeText(value: unknown, added: string, removed: string): string {
  if (typeof value === 'object' && value !== null) {
    const v = value as { added?: unknown; removed?: unknown };
    if (typeof v.added === 'string') return `${added} ${v.added}`;
    if (typeof v.removed === 'string') return `${removed} ${v.removed}`;
  }
  return 'Changed';
}

/** "Maritime Airborne · Sector Lead: Graham Whitlock": each entry names its sector. */
export function leadChange(value: unknown): string {
  const sector =
    typeof value === 'object' && value !== null && 'sector' in value
      ? (value as { sector?: { name?: unknown } }).sector?.name
      : undefined;
  const text = listChangeText(value, 'Sector Lead:', 'No longer Sector Lead:');
  return typeof sector === 'string' ? `${sector} · ${text}` : text;
}

/**
 * The worked example under the PAA options share (APP-151): a $5,000 PAA pays $5,000 in cash, and
 * options on top worth the share of it at the FMV, whole and rounded down (the server's rule).
 */
export function paaExample(
  cashCents: number,
  optionShare: Fraction,
  fmvCents: number,
): { optionsValueCents: number; options: number } {
  const options =
    fmvCents > 0 ? Math.floor((cashCents * optionShare.num) / (optionShare.den * fmvCents)) : 0;
  return {
    optionsValueCents: Math.round((cashCents * optionShare.num) / optionShare.den),
    options,
  };
}

/** "50% of the cash": a PAA's options share as Values and its History say it. */
export function optionShareText(value: unknown): string {
  return isFraction(value) ? `${percentText(value)} of the cash` : 'Not set';
}

/** A budget value edited in place, as the ledger entry it makes (K31). */
export function budgetChange(
  before: number,
  after: number,
): { kind: 'addition' | 'reduction'; amount: number } | null {
  if (!Number.isSafeInteger(before) || !Number.isSafeInteger(after) || after < 0) return null;
  if (after === before) return null;
  return after > before
    ? { kind: 'addition', amount: after - before }
    : { kind: 'reduction', amount: before - after };
}

export type LedgerKind = PoolLedger['entries'][number]['kind'];

/** The ledger's change types as people say them (K31), moves as one type. */
export const LEDGER_KIND_LABEL: Readonly<Record<LedgerKind, string>> = {
  initial: 'Initial budget',
  addition: 'Addition',
  reduction: 'Reduction',
  move_in: 'Move',
  move_out: 'Move',
  top_up: 'Top-up for over-budget',
};

/**
 * A ledger entry's kind in a sentence: "(Terrestrial top-up)", "top-up for Elena Brightwater"
 * (Budget and Ledger boards). Not `LEDGER_KIND_LABEL` lowercased: the label's "Top-up for
 * over-budget" would read "top-up for over-budget for Elena".
 */
export const LEDGER_KIND_WORD: Readonly<Record<LedgerKind, string>> = {
  initial: 'initial budget',
  addition: 'addition',
  reduction: 'reduction',
  move_in: 'move',
  move_out: 'move',
  top_up: 'top-up',
};

export type LedgerFilter = 'all' | 'initial' | 'addition' | 'reduction' | 'move' | 'top_up';

export function ledgerFilterOf(kind: LedgerKind): Exclude<LedgerFilter, 'all'> {
  return kind === 'move_in' || kind === 'move_out' ? 'move' : kind;
}

export interface LedgerRow {
  id: string;
  pool: { id: string; name: string };
  entry: PoolLedger['entries'][number];
  /** The pool's running budget once this entry applies. */
  after: { cashCents: number; options: number };
}

/**
 * Every pool's entries in one list, newest first, each with its pool's running total. Voided
 * entries stay listed but don't count (nothing is deleted).
 */
export function mergeLedgers(ledgers: readonly PoolLedger[]): LedgerRow[] {
  const rows: LedgerRow[] = [];
  for (const ledger of ledgers) {
    let cash = 0;
    let options = 0;
    const oldestFirst = [...ledger.entries].sort((a, b) => a.at.localeCompare(b.at));
    for (const entry of oldestFirst) {
      if (!entry.voided) {
        cash += entry.cashCents;
        options += entry.options;
      }
      rows.push({
        id: entry.id,
        pool: ledger.pool,
        entry,
        after: { cashCents: cash, options },
      });
    }
  }
  return rows.sort(
    (a, b) => b.entry.at.localeCompare(a.entry.at) || a.pool.name.localeCompare(b.pool.name),
  );
}

/** What a pool's ledger adds up to: where it started and the net of everything since. */
export function ledgerSummary(ledger: PoolLedger): {
  startedCents: number | null;
  netCents: number;
  entries: number;
} {
  const live = ledger.entries.filter((e) => !e.voided);
  const initial = live.filter((e) => e.kind === 'initial');
  const startedCents = initial.length === 0 ? null : initial.reduce((s, e) => s + e.cashCents, 0);
  const netCents = live.filter((e) => e.kind !== 'initial').reduce((s, e) => s + e.cashCents, 0);
  return { startedCents, netCents, entries: ledger.entries.length };
}

/** "+$5,000", "−$4,664", "$0". */
export function signedMoney(cents: number): string {
  if (cents === 0) return '$0';
  return cents > 0 ? `+${formatMoney(cents)}` : formatMoney(cents);
}

/** "+200 options", "−50 options". */
export function signedOptions(options: number): string {
  const n = formatCount(Math.abs(options));
  const word = Math.abs(options) === 1 ? 'option' : 'options';
  if (options === 0) return `0 ${word}`;
  return `${options > 0 ? '+' : '−'}${n} ${word}`;
}

/** "Oct 2, 2026" for a value that applies from a date, or "today" when it's today. */
export function fromText(date: string, today: string): string {
  return date === today ? 'today' : formatDate(date);
}

/** "With 3 days left, and on the last day": the PAA election reminders. */
export function reminderText(value: unknown): string {
  if (!Array.isArray(value) || value.length === 0) return 'No reminders';
  const days = (value as unknown[])
    .filter((d): d is number => typeof d === 'number')
    .sort((a, b) => b - a);
  const parts = days.map((d) =>
    d === 0 ? 'on the last day' : `with ${plural(d, 'day', 'days')} left`,
  );
  if (parts.length === 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1] ?? ''}`;
}

/** "On, 2 days after the deadline", or "Off": the manager nudge once an election is late. */
export function nudgeText(value: unknown): string {
  if (typeof value !== 'object' || value === null || !('enabled' in value)) return 'Off';
  const v = value as { enabled: boolean; daysLate?: number };
  return v.enabled ? `On, ${plural(v.daysLate ?? 0, 'day', 'days')} after the deadline` : 'Off';
}
